// gameSicBo.js
'use strict';
import gsap from 'gsap';
import { toPng } from 'html-to-image';

import { game as gameDebug } from '../resource/debug.js';
import loadScriptTag from '../resource/loadScriptTag.js';
import numberWithCommas from '../resource/numberWithCommas.js';
import i18n from '../resource/i18n.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color } from '../style/variables.js';

import Diamond12Image from '../../img/ic_diamond_tealblue_12.svg';
import Diamond24Image from '../../img/ic_diamond_tealblue_24.svg';
import ShopIconSource from '../../img/ic_diamond_shop.svg';
import BackgroundImageSource from '../../img/Sicbo_BG.png';
import GameStatusToasterImageSource from '../../img/Sicbo_Tip.png';
import SicBoTableImageSource from '../../img/Sicbo_Table.png';
import SicBoDealerImageSource from '../../img/Sicbo_Girl.png';
import SicBoChips100ImageSource from '../../img/Sicbo_Chips_100.png';
import SicBoChips1000ImageSource from '../../img/Sicbo_Chips_1000.png';
import SicBoChips10kImageSource from '../../img/Sicbo_Chips_10K.png';
import SicBoChips100kImageSource from '../../img/Sicbo_Chips_100K.png';
import SicBoDice1ImageSource from '../../img/Sicbo_dice_01.png';
import SicBoDice2ImageSource from '../../img/Sicbo_dice_02.png';
import SicBoDice3ImageSource from '../../img/Sicbo_dice_03.png';
import SicBoDice4ImageSource from '../../img/Sicbo_dice_04.png';
import SicBoDice5ImageSource from '../../img/Sicbo_dice_05.png';
import SicBoDice6ImageSource from '../../img/Sicbo_dice_06.png';
import SicBoDrawResultImageSource from '../../img/Sicbo_Result.png';
import SicBoWinResultImageSource from '../../img/Sicbo_Win.png';
import SicBoUserDiamondsImageSource from '../../img/Sicbo_Diamonds.png';

// TODO: remote config (start)
export const GAME_DURATION_MSEC = 30000; // 30 seconds
export const GAME_TUTORIAL_MAX_STEP = 3;
const PIXI_VERSION = '7.2.4';
const TOASTER_DURATION_MSEC = 1500;
// TODO: remote config (end)
const gameDebugLog = gameDebug.extend('log:gameSicBo');
const gameError = gameDebug.extend('error:gameSicBo');

export const GameStateEnum = {
  LOADING_PIXI: 'LOADING-PIXI',
  INITING_GAME: 'INITING-GAME',
  WAITING_GAME_ID: 'WAITING-GAME-ID',
  PREPARING: 'preparing',
  BETTING: 'betting',
  ROLLING: 'rolling',
  DRAWING: 'drawing',
  RESETTING: 'resetting',
  FINISHED: 'finished',
};

export const GameStateDurationSecondEnum = {
  PREPARING: 2,
  BETTING: 15,
  ROLLING: 4,
  DRAWING: 8,
  RESETTING: 2,
  FINISHED: 0.5,
};

export const BetChoiceEnum = {
  BIG: 'big',
  SMALL: 'small',
  TRIPLES: 'triples',
};
const BetTextKeyMap = {
  [BetChoiceEnum.BIG]: 'sic_bo_big',
  [BetChoiceEnum.SMALL]: 'sic_bo_small',
  [BetChoiceEnum.TRIPLES]: 'sic_bo_triple',
};
const BetAmountNameMap = {
  [100]: '100',
  [1000]: '1000',
  [10000]: '10K',
  [100000]: '100K',
};
const DiceNameMap = {
  [1]: 'diceOne',
  [2]: 'diceTwo',
  [3]: 'diceThree',
  [4]: 'diceFour',
  [5]: 'diceFive',
  [6]: 'diceSix',
};
const SicBoColor = {
  slotBorder: '#7bafab',
  slotBackground: '#25166d',
  betBorder: '#adadea88',
  betBackground: '#472c9b',
  chipBorder: '#3effee',
};

export const DisplayObjectNames = {
  ROOT: 'root',

  BACKGROUND_CONTAINER: 'backgroundContainer',
  TABLE_CONTAINER: 'tableContainer',
  FOREGROUND_CONTAINER: 'foregroundContainer',
  UI_CONTAINER: 'uiContainer',
  TOASTER_CONTAINER: 'ToasterContainer',
  MODAL_CONTAINER: 'modalContainer',

  BACKGROUND_SPRITE: 'backgroundSprite',
  HEADER: 'header',
  SCENE_TITLE: 'sceneTitle',
  COUNTDOWN: 'countdown',

  TOASTER_BACKGROUND: 'toasterBackground',

  GAME_STATUS_TOASTER: 'gameStatusToaster',
  GAME_STATUS_TOASTER_SPRITE: 'gameStatusToasterSprite',
  GAME_STATUS_TOASTER_TITLE: 'gameStatusToasterTitle',

  BET_TOASTERS: 'betToasters',
  BET_TOASTER: 'betToaster',
  BET_TOASTER_CHOICE_TEXT: 'betToasterChoiceText',
  BET_TOASTER_DIAMONS_SPRITE: 'betToasterDiamondSprite',
  BET_TOASTER_AMOUNT_TEXT: 'betToasterAmountText',

  DRAW_RESULT_TOASTER: 'drawResultToaster',
  DRAW_RESULT_DICE_1ST: 'drawResultDice1st',
  DRAW_RESULT_DICE_2ND: 'drawResultDice2nd',
  DRAW_RESULT_DICE_3RD: 'drawResultDice3rd',
  DRAW_RESULT_CONTENT: 'drawResultContent',
  DRAW_RESULT_CONTENT_SPRITE: 'drawResultContentSprite',
  DRAW_RESULT_CONTENT_TEXT: 'drawResultContentText',

  BET_RESULT_TOASTER: 'betResultToaster',
  BET_RESULT_WIN_TOASTER: 'betResultWinToaster',
  BET_RESULT_WIN_SPRITE: 'betResultWinSprite',
  BET_RESULT_WIN_CONTENT_SPRITE: 'betResultWinContentSprite',
  BET_RESULT_SUMMARY: 'betResultSummary',
  BET_RESULT_SUMMARY_CONTENT_SPRITE: 'betResultSummaryContentSprite',

  TABLE: 'table',
  TABLE_SPRITE: 'tableSprite',

  SMALL_SLOT: 'smallSlot',
  SMALL_SLOT_TITLE: 'smallSlotTitle',
  SMALL_SLOT_SUBTITLE: 'smallSlotSubtitle',
  SMALL_BETS: 'smallBets',
  SMALL_BETS_NUMBER: 'smallBetsNumber',
  BIG_SLOT: 'bigSlot',
  BIG_SLOT_TITLE: 'bigSlotTitle',
  BIG_SLOT_SUBTITLE: 'bigSlotSubtitle',
  BIG_BETS: 'bigBets',
  BIG_BETS_NUMBER: 'bigBetsNumber',
  TRIPLES_SLOT: 'triplesSlot',
  TRIPLES_SLOT_TITLE: 'triplesSlotTitle',
  TRIPLES_SLOT_SUBTITLE: 'triplesSlotSubtitle',
  TRIPLES_BETS: 'triplesBets',
  TRIPLES_BETS_NUMBER: 'triplesBetsNumber',

  DEALER_SPRITE: 'dealerSprite',

  BETTED_CHIPS: 'bettedChips',
  BETTED_CHIP: 'bettedChip',

  USER_POINTS: 'userPoints',
  USER_POINTS_SPRITE: 'userPointsSprite',
  USER_POINTS_DIAMOND_SPRITE: 'userPointsDiamondSprite',
  USER_POINTS_NUMBER: 'userPointsNumber',

  CHIPS_SELECTOR: 'chipsSelector',
  CHIPS_SELECTOR_100: 'chipsSelector100',
  CHIPS_SELECTOR_100_SPRITE: 'chipsSelector100Sprite',
  CHIPS_SELECTOR_100_HALO: 'chipsSelector100Halo',
  CHIPS_SELECTOR_1000: 'chipsSelector1000',
  CHIPS_SELECTOR_1000_SPRITE: 'chipsSelector1000Sprite',
  CHIPS_SELECTOR_1000_HALO: 'chipsSelector1000Halo',
  CHIPS_SELECTOR_10K: 'chipsSelector10k',
  CHIPS_SELECTOR_10K_SPRITE: 'chipsSelector10kSprite',
  CHIPS_SELECTOR_10K_HALO: 'chipsSelector10kHalo',
  CHIPS_SELECTOR_100K: 'chipsSelector100k',
  CHIPS_SELECTOR_100K_SPRITE: 'chipsSelector100kSprite',
  CHIPS_SELECTOR_100K_HALO: 'chipsSelector100kHalo',

  ROLL_VIDEO_TOASTER: 'rollVideoToaster',
  ROLL_VIDEO_START_SPRITE: 'rollVideoStartSprite',
  ROLL_VIDEO_MIDDLE_SPRITE: 'rollVideoMiddleSprite',
  ROLL_VIDEO_END_SPRITE: 'rollVideoEndSprite',
};

const DomResourceNames = {
  HIDDEN_CONTAINER: 'sicBoHiddenContainer',

  BET_RESULT_WIN_CONTENT: 'betResultWinContent',
  BET_RESULT_WIN_DIAMOND: 'betResultWinDiamond',
  BET_RESULT_WIN_NUMBER: 'betResultWinNumber',

  BET_RESULT_SUMMARY_CONTENT: 'betResultSummaryContent',
  BET_RESULT_SUMMARY_INPUT_TEXT: 'betResultSummaryInputText',
  BET_RESULT_SUMMARY_INPUT_DIAMOND: 'betResultSummaryInputDiamond',
  BET_RESULT_SUMMARY_INPUT_NUMBER: 'betResultSummaryInputNumber',
  BET_RESULT_SUMMARY_OUTPUT_TEXT: 'betResultSummaryOutputText',
  BET_RESULT_SUMMARY_OUTPUT_DIAMOND: 'betResultSummaryOutputDiamond',
  BET_RESULT_SUMMARY_OUTPUT_NUMBER: 'betResultSummaryOutputNumber',

  ROLL_VIDEOS: 'rollVideos',
  ROLL_VIDEO_START: 'rollVideoStart',
  ROLL_VIDEO_MIDDLE: 'rollVideoMiddle',
  ROLL_VIDEO_END: 'rollVideoEnd',
};

let pixi = null;
export const loadPixiJSPromise = async () => {
  const log = gameDebugLog.extend('loadPixiJSPromise');
  const errorLog = gameError.extend('loadPixiJSPromise');
  try {
    log('start loading pixi');
    await loadScriptTag({
      id: `pixijs-${PIXI_VERSION.replace(/[\W_]/g, '-')}`,
      src: `https://unpkg.com/pixi.js@${PIXI_VERSION}/dist/pixi.min.js`,
    });
    pixi = window.PIXI;
    log('done loading pixi', window.PIXI);
  } catch (error) {
    errorLog('load pixi error', error);
  }
  return;
};

const manifest = {
  bundles: Object.keys(GameStateEnum).map(stateKey => {
    return {
      name: GameStateEnum[stateKey],
      assets: [
        {
          name: DisplayObjectNames.BACKGROUND_SPRITE,
          srcs: BackgroundImageSource,
        },
        {
          name: DisplayObjectNames.GAME_STATUS_TOASTER_SPRITE,
          srcs: GameStatusToasterImageSource,
        },
        {
          name: DisplayObjectNames.TABLE_SPRITE,
          srcs: SicBoTableImageSource,
        },
        {
          name: DisplayObjectNames.DEALER_SPRITE,
          srcs: SicBoDealerImageSource,
        },
        {
          name: DisplayObjectNames.USER_POINTS_SPRITE,
          srcs: SicBoUserDiamondsImageSource,
        },
        {
          name: DisplayObjectNames.USER_POINTS_DIAMOND_SPRITE,
          srcs: ShopIconSource,
        },
        {
          name: DisplayObjectNames.CHIPS_SELECTOR_100_SPRITE,
          srcs: SicBoChips100ImageSource,
        },
        {
          name: DisplayObjectNames.CHIPS_SELECTOR_1000_SPRITE,
          srcs: SicBoChips1000ImageSource,
        },
        {
          name: DisplayObjectNames.CHIPS_SELECTOR_10K_SPRITE,
          srcs: SicBoChips10kImageSource,
        },
        {
          name: DisplayObjectNames.CHIPS_SELECTOR_100K_SPRITE,
          srcs: SicBoChips100kImageSource,
        },
      ],
    };
  }),
};

// Wait for this PR to release
// https://github.com/pixijs/pixijs/pull/9376
/*
const rollingBundle = manifest.bundles.find(
  bundle => bundle.name === GameStateEnum.ROLLING
);
if (rollingBundle) {
  rollingBundle.assets.push(
    ...[
      {
        name: DisplayObjectNames.ROLL_VIDEO_START_SPRITE,
        srcs: SicBoRollStartVideoSource,
        format: 'mp4',
      },
      {
        name: DisplayObjectNames.ROLL_VIDEO_MIDDLE_SPRITE,
        srcs: SicBoRollMiddleVideoSource,
        format: 'mp4',
      },
    ]
  );
}
*/
const drawingBundle = manifest.bundles.find(
  bundle => bundle.name === GameStateEnum.DRAWING
);
if (drawingBundle) {
  drawingBundle.assets.push(
    ...[
      // Wait for this PR to release
      // https://github.com/pixijs/pixijs/pull/9376
      /*
      {
        name: DisplayObjectNames.ROLL_VIDEO_END_SPRITE,
        srcs: SicBoRollEndVideoSource,
        format: 'mp4',
      },
      */
      { name: DiceNameMap[1], srcs: SicBoDice1ImageSource },
      { name: DiceNameMap[2], srcs: SicBoDice2ImageSource },
      { name: DiceNameMap[3], srcs: SicBoDice3ImageSource },
      { name: DiceNameMap[4], srcs: SicBoDice4ImageSource },
      { name: DiceNameMap[5], srcs: SicBoDice5ImageSource },
      { name: DiceNameMap[6], srcs: SicBoDice6ImageSource },
      {
        name: DisplayObjectNames.DRAW_RESULT_CONTENT_SPRITE,
        srcs: SicBoDrawResultImageSource,
      },
      {
        name: DisplayObjectNames.BET_RESULT_WIN_SPRITE,
        srcs: SicBoWinResultImageSource,
      },
    ]
  );
}

const loadImage = ({ src }) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = error => reject(error);
    image.src = src;
  });

const getSlotDisplayObjects = ({
  name = 'BIG',
  choice = BetChoiceEnum.BIG,
  titleKey = 'sic_bo_big',
  rate = 2,
  subtitleKey = 'sic_bo_big_detail',
  position = { x: '4px', y: '46px' },
  size = { x: 144, y: 44 },
  betsPosition = { x: '88px', y: '-20px' },
}) => {
  const slotName = DisplayObjectNames[`${name}_SLOT`];
  const titleName = DisplayObjectNames[`${name}_SLOT_TITLE`];
  const subtitleName = DisplayObjectNames[`${name}_SLOT_SUBTITLE`];
  const betsName = DisplayObjectNames[`${name}_BETS`];
  const betsNumberName = DisplayObjectNames[`${name}_BETS_NUMBER`];
  if (!(slotName && titleName && subtitleName && betsName && betsNumberName)) {
    return {};
  }
  return {
    [slotName]: {
      name: slotName,
      geometry: { position },
      init: async () => {
        const graphic = new pixi.Graphics();
        graphic.lineStyle({ width: 2, color: SicBoColor.slotBorder });
        graphic.beginFill(SicBoColor.slotBackground, 0.5);
        graphic.drawRoundedRect(0, 0, size.x, size.y, 8);
        graphic.endFill();
        graphic.cursor = 'not-allowed';
        graphic.on('pointertap', () => {
          const gameCore = getGameCore();
          const activeChip = gameCore?.user?.activeChip;
          if (!activeChip || isNaN(activeChip)) {
            gameError('missing bet data', { activeChip });
            return;
          }
          gameCore.eventTarget?.bet({
            choice,
            amount: gameCore.user.activeChip,
          });
        });
        return graphic;
      },
    },
    [titleName]: {
      name: titleName,
      geometry: {
        position: { x: `${size.x / 2}px`, y: '4px' },
        anchor: { x: 0.5, y: 0 },
      },
      init: async () => {
        const style = new pixi.TextStyle({
          fill: color.white,
          fontSize: 22,
        });
        const text = new pixi.Text(
          `${i18n.t(titleKey, {
            ns: TranslationNamespace.LIVESTREAM,
          })}  ${rate}X`,
          style
        );
        return text;
      },
    },
    [subtitleName]: {
      name: subtitleName,
      geometry: {
        position: { x: `${size.x / 2}px`, y: '30px' },
        anchor: { x: 0.5, y: 0 },
      },
      init: async () => {
        const style = new pixi.TextStyle({
          fill: color.sky,
          fontSize: 8,
        });
        const text = new pixi.Text(
          i18n.t(subtitleKey, { ns: TranslationNamespace.LIVESTREAM }),
          style
        );
        return text;
      },
    },
    [betsName]: {
      name: betsName,
      geometry: {
        position: betsPosition,
      },
      init: async () => {
        const graphic = new pixi.Graphics();
        graphic.lineStyle({ width: 1, color: SicBoColor.betBorder });
        graphic.beginFill(SicBoColor.betBackground, 0.8);
        graphic.drawRoundedRect(-20, 0, 40, 12, 6);
        graphic.endFill();
        return graphic;
      },
    },
    [betsNumberName]: {
      name: betsNumberName,
      geometry: {
        position: { x: 0, y: '6px' },
        anchor: { x: 0.5, y: 0.5 },
      },
      init: async () => {
        const style = new pixi.TextStyle({
          fill: color.white,
          fontSize: 8,
          letterSpacing: 1,
        });
        const text = new pixi.Text('0', style);
        return text;
      },
    },
  };
};

const getChipSelectorDisplayObjects = ({
  name = '100',
  position = { x: '30px', y: '-4px' },
  amount = 100,
}) => {
  const slotName = DisplayObjectNames[`CHIPS_SELECTOR_${name}`];
  const spriteName = DisplayObjectNames[`CHIPS_SELECTOR_${name}_SPRITE`];
  const haloName = DisplayObjectNames[`CHIPS_SELECTOR_${name}_HALO`];
  return {
    [slotName]: {
      name: slotName,
      geometry: {
        anchor: { x: 0, y: 1 },
        position,
      },
      init: async () => {
        const graphic = new pixi.Graphics();
        graphic.beginFill(color.white, 0);
        graphic.drawRect(0, -30, 30, 30);
        graphic.endFill();

        graphic.eventMode = 'static';
        graphic.cursor = 'pointer';
        graphic.on('pointertap', () => {
          const gameCore = getGameCore();
          if (gameCore.user.activeChip !== amount) {
            gameCore.eventTarget?.chooseChip({ amount });
          }
        });
        graphic.on('pointerover', () => {
          const gameCore = getGameCore();
          if (gameCore.user) {
            gameCore.user.hoverChip = amount;
          }
        });
        graphic.on('pointerout', () => {
          const gameCore = getGameCore();
          if (gameCore.user) {
            gameCore.user.hoverChip = null;
          }
        });

        return graphic;
      },
    },
    [spriteName]: {
      name: spriteName,
      geometry: {
        anchor: { x: 0.5, y: 0.5 },
        position: { x: '15px', y: '-15px' },
        size: { x: '30px', y: '30px' },
      },
      init: async () => {
        const sprite = pixi.Sprite.from(spriteName);
        return sprite;
      },
    },
    [haloName]: {
      name: haloName,
      geometry: {
        anchor: { x: 0.5, y: 0.5 },
        position: { x: '15px', y: '-16px' },
      },
      init: async () => {
        const graphic = new pixi.Graphics();
        graphic.lineStyle({ width: 3, color: SicBoColor.chipBorder });
        graphic.beginFill(color.white, 0);
        graphic.drawCircle(0, 0, 14);
        graphic.endFill();
        graphic.visible = false;
        return graphic;
      },
    },
  };
};
const getRollVideoSpriteDisplayObject = ({ name, videoDomName }) => {
  return {
    name,
    geometry: {
      anchor: { x: 0.5, y: 0.5 },
      position: { x: 0, y: 0 },
    },
    init: async ({ gameCore }) => {
      const [videoElement] =
        gameCore?.domResources[
          DomResourceNames.ROLL_VIDEOS
        ]?.getElementsByClassName(videoDomName) || [];
      if (!videoElement) {
        return;
      }
      const resource = new pixi.VideoResource(videoElement, {
        autoPlay: false,
      });
      const sprite = pixi.Sprite.from(resource);
      sprite.visible = false;
      return sprite;
    },
  };
};

const displayObjects = {
  [DisplayObjectNames.BACKGROUND_SPRITE]: {
    name: DisplayObjectNames.BACKGROUND_SPRITE,
    geometry: {
      anchor: { x: 0.5, y: 1 },
      position: { x: 0.5, y: 1 },
      size: { x: '768px', y: '228px' },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(DisplayObjectNames.BACKGROUND_SPRITE);
      return sprite;
    },
  },
  [DisplayObjectNames.HEADER]: {
    name: DisplayObjectNames.HEADER,
    geometry: {
      size: { x: 1, y: '36px' },
      anchor: { x: 0, y: 0 },
    },
    init: async ({ gameCore }) => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.purple);
      graphic.drawRect(0, 0, gameCore.app.screen.width, 36);
      graphic.endFill();
      return graphic;
    },
  },
  [DisplayObjectNames.SCENE_TITLE]: {
    name: DisplayObjectNames.SCENE_TITLE,
    geometry: {
      position: { x: '12px', y: '18px' },
      anchor: { x: 0, y: 0.5 },
    },
    init: async () => {
      const style = new pixi.TextStyle({
        fill: '#ffffff',
        fontSize: 14,
      });
      const text = new pixi.Text(GameStateEnum.INITING_GAME, style);
      return text;
    },
  },
  [DisplayObjectNames.COUNTDOWN]: {
    name: DisplayObjectNames.COUNTDOWN,
    geometry: {
      position: { x: 0.5, y: '36px' },
      anchor: { x: 0.5, y: 0 },
    },
    init: async () => {
      const style = new pixi.TextStyle({
        dropShadow: true,
        dropShadowColor: '#89098b',
        fill: '#ffffff',
        fontSize: 60,
        fontWeight: 600,
        letterSpacing: 18,
      });
      const text = new pixi.Text('00', style);
      return text;
    },
  },
  [DisplayObjectNames.TOASTER_BACKGROUND]: {
    name: DisplayObjectNames.TOASTER_BACKGROUND,
    geometry: {
      size: { x: 1, y: 1 },
      position: { x: 0, y: 0 },
    },
    init: async ({ gameCore }) => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.black, 0.8);
      graphic.drawRect(
        0,
        0,
        gameCore.app.screen.width,
        gameCore.app.screen.height
      );
      graphic.endFill();
      graphic.alpha = 0;
      graphic.visible = false;

      graphic.tween = gsap.effects.fadeInOut(graphic, {
        onComplete: () => {
          graphic.visible = false;
        },
      });
      graphic.tween.pause();

      return graphic;
    },
  },
  [DisplayObjectNames.GAME_STATUS_TOASTER]: {
    name: DisplayObjectNames.GAME_STATUS_TOASTER,
    geometry: {
      position: { x: 0.5, y: 0.57 },
    },
    init: async ({ gameCore }) => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.salmon, 0);
      graphic.drawRect(-152, -45, 304, 90);
      graphic.endFill();
      graphic.pivot.set(0.5, 0.5);

      graphic.tween = gsap.effects.flip(graphic, {
        width: 304,
        height: 90,
        onComplete: () => {
          graphic.visible = false;
          graphic.width = 304;
          graphic.height = 90;
          gameCore?.eventTarget?.emitTweenEvent({
            name: DisplayObjectNames.GAME_STATUS_TOASTER,
            type: 'tweenComplete',
            target: graphic,
          });
        },
      });
      graphic.tween.pause();
      return graphic;
    },
  },
  [DisplayObjectNames.GAME_STATUS_TOASTER_SPRITE]: {
    name: DisplayObjectNames.GAME_STATUS_TOASTER_SPRITE,
    geometry: {
      position: { x: 0, y: 0 },
      size: { x: '304px', y: '90px' },
      anchor: { x: 0.5, y: 0.5 },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(
        DisplayObjectNames.GAME_STATUS_TOASTER_SPRITE
      );
      return sprite;
    },
  },
  [DisplayObjectNames.GAME_STATUS_TOASTER_TITLE]: {
    name: DisplayObjectNames.GAME_STATUS_TOASTER_TITLE,
    geometry: {
      position: { x: 0, y: 0 },
      anchor: { x: 0.5, y: 0.5 },
    },
    init: async () => {
      const style = new pixi.TextStyle({
        fill: color.white,
        fontSize: 22,
        fontWeight: 600,
      });
      const text = new pixi.Text(
        i18n.t('sic_bo_start', { ns: TranslationNamespace.LIVESTREAM }),
        style
      );
      return text;
    },
  },
  [DisplayObjectNames.BET_TOASTERS]: {
    name: DisplayObjectNames.BET_TOASTERS,
    geometry: {
      position: { x: 0.5, y: '176px' },
    },
    init: async () => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.peach, 0);
      graphic.drawRect(-80, -16, 160, 32);
      graphic.endFill();
      return graphic;
    },
  },
  [DisplayObjectNames.BET_TOASTER]: {
    name: DisplayObjectNames.BET_TOASTER,
    geometry: {
      position: { x: 0, y: 0 },
    },
    init: async ({ width }) => {
      const graphic = new pixi.Graphics();
      graphic.lineStyle({ width: 1, color: SicBoColor.betBorder });
      graphic.beginFill(SicBoColor.betBackground, 0.8);
      graphic.drawRoundedRect(-width / 2, -14, width, 28, 4);
      graphic.endFill();
      // graphic.alpha = 0;
      return graphic;
    },
  },
  [DisplayObjectNames.BET_TOASTER_CHOICE_TEXT]: {
    name: DisplayObjectNames.BET_TOASTER_CHOICE_TEXT,
    geometry: {
      position: { x: '4px', y: 0 },
      anchor: { x: 0, y: 0.5 },
    },
    init: async ({ bet }) => {
      const style = new pixi.TextStyle({
        fill: color.white,
        fontSize: 12,
      });
      const textContent = i18n.t(BetTextKeyMap[bet.choice], {
        ns: TranslationNamespace.LIVESTREAM,
      });
      const text = new pixi.Text(textContent, style);
      return text;
    },
  },
  [DisplayObjectNames.BET_TOASTER_AMOUNT_TEXT]: {
    name: DisplayObjectNames.BET_TOASTER_AMOUNT_TEXT,
    geometry: {
      position: { x: '4px', y: 0 },
      anchor: { x: 0, y: 0.5 },
    },
    init: async ({ bet }) => {
      const style = new pixi.TextStyle({
        fill: color.tealBlue,
        fontSize: 12,
      });
      const text = new pixi.Text(
        numberWithCommas({ number: bet.amount }),
        style
      );
      return text;
    },
  },
  [DisplayObjectNames.DRAW_RESULT_TOASTER]: {
    name: DisplayObjectNames.DRAW_RESULT_TOASTER,
    geometry: {
      position: { x: 0.5, y: 0.57 },
    },
    init: async () => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.yellow, 0);
      graphic.drawRect(-152, -92, 304, 184);
      graphic.endFill();
      graphic.pivot.set(0.5, 0.5);
      // graphic.visible = false;
      return graphic;
    },
  },
  [DisplayObjectNames.DRAW_RESULT_DICE_1ST]: {
    name: DisplayObjectNames.DRAW_RESULT_DICE_1ST,
    geometry: {
      position: { x: '-90px', y: '-8px' },
      size: { x: '82px', y: '82px' },
      anchor: { x: 0.5, y: 1 },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(DiceNameMap[1]);
      return sprite;
    },
  },
  [DisplayObjectNames.DRAW_RESULT_DICE_2ND]: {
    name: DisplayObjectNames.DRAW_RESULT_DICE_2ND,
    geometry: {
      position: { x: 0, y: '-8px' },
      size: { x: '82px', y: '82px' },
      anchor: { x: 0.5, y: 1 },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(DiceNameMap[2]);
      return sprite;
    },
  },
  [DisplayObjectNames.DRAW_RESULT_DICE_3RD]: {
    name: DisplayObjectNames.DRAW_RESULT_DICE_3RD,
    geometry: {
      position: { x: '90px', y: '-8px' },
      size: { x: '82px', y: '82px' },
      anchor: { x: 0.5, y: 1 },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(DiceNameMap[3]);
      return sprite;
    },
  },
  [DisplayObjectNames.DRAW_RESULT_CONTENT]: {
    name: DisplayObjectNames.DRAW_RESULT_CONTENT,
    geometry: {
      position: { x: 0, y: '8px' },
    },
    init: async () => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.yellow, 0);
      graphic.drawRect(-110, 0, 220, 72);
      graphic.endFill();
      return graphic;
    },
  },
  [DisplayObjectNames.DRAW_RESULT_CONTENT_SPRITE]: {
    name: DisplayObjectNames.DRAW_RESULT_CONTENT_SPRITE,
    geometry: {
      position: { x: 0, y: 0 },
      size: { x: '220px', y: '72px' },
      anchor: { x: 0.5, y: 0 },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(
        DisplayObjectNames.DRAW_RESULT_CONTENT_SPRITE
      );
      return sprite;
    },
  },
  [DisplayObjectNames.DRAW_RESULT_CONTENT_TEXT]: {
    name: DisplayObjectNames.DRAW_RESULT_CONTENT_TEXT,
    geometry: {
      position: { x: 0, y: '36px' },
      anchor: { x: 0.5, y: 0.5 },
    },
    init: async () => {
      const style = new pixi.TextStyle({
        fill: color.white,
        fontSize: 22,
      });
      const text = new pixi.Text('18 Triples', style);
      return text;
    },
  },
  [DisplayObjectNames.BET_RESULT_TOASTER]: {
    name: DisplayObjectNames.BET_RESULT_TOASTER,
    geometry: {
      position: { x: 0.5, y: 0.57 },
    },
    init: async () => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.yellow, 0);
      graphic.drawRect(-152, -70, 304, 140);
      graphic.endFill();
      graphic.pivot.set(0.5, 0.5);
      return graphic;
    },
  },
  [DisplayObjectNames.BET_RESULT_WIN_TOASTER]: {
    name: DisplayObjectNames.BET_RESULT_WIN_TOASTER,
    geometry: {
      position: { x: 0, y: '-20px' },
      size: { x: '304px', y: '100px' },
    },
    init: async () => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.yellow, 0);
      graphic.drawRect(-152, -50, 304, 100);
      graphic.endFill();
      graphic.pivot.set(0.5, 0.5);
      graphic.visible = false;

      graphic.tween = gsap.effects.flip(graphic, {
        width: 304,
        height: 100,
        onComplete: () => {
          graphic.visible = false;
          graphic.width = 304;
          graphic.height = 100;
        },
      });
      graphic.tween.pause();
      return graphic;
    },
  },
  [DisplayObjectNames.BET_RESULT_WIN_SPRITE]: {
    name: DisplayObjectNames.BET_RESULT_WIN_SPRITE,
    geometry: {
      position: { x: 0, y: 0 },
      size: { x: '304px', y: '100px' },
      anchor: { x: 0.5, y: 0.5 },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(DisplayObjectNames.BET_RESULT_WIN_SPRITE);
      return sprite;
    },
  },
  [DisplayObjectNames.BET_RESULT_WIN_CONTENT_SPRITE]: {
    name: DisplayObjectNames.BET_RESULT_WIN_CONTENT_SPRITE,
    geometry: {
      position: { x: 0, y: '10px' },
      anchor: { x: 0.5, y: 0.5 },
    },
    init: async ({ gameCore, earnedPoints }) => {
      const content =
        gameCore.domResources[DomResourceNames.BET_RESULT_WIN_CONTENT];
      const [number] = content.getElementsByClassName(
        DomResourceNames.BET_RESULT_WIN_NUMBER
      );
      if (earnedPoints) {
        number.textContent = numberWithCommas({ number: earnedPoints });
      }
      const hiddenContainer =
        gameCore.domResources[DomResourceNames.HIDDEN_CONTAINER];
      hiddenContainer.appendChild(content);
      const dataUrl = await toPng(content, {
        backgroundColor: '#fff0',
        pixelRatio: 2,
      });
      content.parentNode?.removeChild(content);
      const image = await loadImage({ src: dataUrl });
      const sprite = new pixi.Sprite(pixi.Texture.from(image));
      sprite.scale.set(0.5);
      sprite.name = DisplayObjectNames.BET_RESULT_WIN_CONTENT_SPRITE;
      return sprite;
    },
  },
  [DisplayObjectNames.BET_RESULT_SUMMARY]: {
    name: DisplayObjectNames.BET_RESULT_SUMMARY,
    geometry: {
      position: { x: 0, y: '56px' },
    },
    init: async ({ width = 178 }) => {
      const graphic = new pixi.Graphics();
      graphic.lineStyle({ width: 1, color: SicBoColor.betBorder });
      graphic.beginFill(SicBoColor.betBackground, 0.8);
      graphic.drawRoundedRect(-0.5 * width, -14, width, 28, 4);
      graphic.endFill();
      graphic.pivot.set(0.5, 0.5);
      graphic.visible = false;

      graphic.tween = gsap.effects.flip(graphic, {
        width,
        height: 28,
        onComplete: () => {
          graphic.visible = false;
          graphic.tween.kill();
          graphic.tween = null;
          graphic.parentNode?.removeChild(graphic);
          graphic.destroy();
        },
      });
      graphic.tween.pause();
      return graphic;
    },
  },
  [DisplayObjectNames.BET_RESULT_SUMMARY_CONTENT_SPRITE]: {
    name: DisplayObjectNames.BET_RESULT_SUMMARY_CONTENT_SPRITE,
    geometry: {
      position: { x: 0, y: 0 },
      anchor: { x: 0.5, y: 0.5 },
    },
    init: async ({ gameCore, inputNumber, outputNumber }) => {
      const content =
        gameCore.domResources[DomResourceNames.BET_RESULT_SUMMARY_CONTENT];
      const [inputText] = content.getElementsByClassName(
        DomResourceNames.BET_RESULT_SUMMARY_INPUT_NUMBER
      );
      const [outputText] = content.getElementsByClassName(
        DomResourceNames.BET_RESULT_SUMMARY_OUTPUT_NUMBER
      );
      inputText.textContent = numberWithCommas({ number: inputNumber });
      outputText.textContent = numberWithCommas({ number: outputNumber });
      const hiddenContainer =
        gameCore.domResources[DomResourceNames.HIDDEN_CONTAINER];
      hiddenContainer.appendChild(content);
      const dataUrl = await toPng(content, {
        backgroundColor: '#fff0',
        pixelRatio: 2,
      });
      content.parentNode?.removeChild(content);
      const image = await loadImage({ src: dataUrl });
      const sprite = new pixi.Sprite(pixi.Texture.from(image));
      sprite.name = DisplayObjectNames.BET_RESULT_SUMMARY_CONTENT_SPRITE;
      sprite.scale.set(0.5);
      return sprite;
    },
  },
  [DisplayObjectNames.TABLE]: {
    name: DisplayObjectNames.TABLE,
    geometry: {
      position: { x: 0.5, y: '80px' },
    },
    init: async () => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.yellow, 0);
      graphic.drawRect(-218, 0, 436, 186);
      graphic.endFill();
      return graphic;
    },
  },
  [DisplayObjectNames.TABLE_SPRITE]: {
    name: DisplayObjectNames.TABLE_SPRITE,
    geometry: {
      anchor: { x: 0.5, y: 0 },
      position: { x: 0, y: 0 },
      size: { x: '436px', y: '186px' },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(DisplayObjectNames.TABLE_SPRITE);
      return sprite;
    },
  },
  [DisplayObjectNames.DEALER_SPRITE]: {
    name: DisplayObjectNames.DEALER_SPRITE,
    geometry: {
      position: { x: 0.5, y: '80px' },
      anchor: { x: 0.5, y: 0.5 },
      size: { x: '105px', y: '75px' },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(DisplayObjectNames.DEALER_SPRITE);
      return sprite;
    },
  },
  [DisplayObjectNames.BETTED_CHIPS]: {
    name: DisplayObjectNames.BETTED_CHIPS,
    geometry: {
      position: { x: 0.5, y: '80px' },
    },
    init: async () => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.yellow, 0);
      graphic.drawRect(-218, 0, 436, 186);
      graphic.endFill();
      return graphic;
    },
  },
  [DisplayObjectNames.BETTED_CHIP]: {
    name: DisplayObjectNames.BETTED_CHIP,
    geometry: {
      anchor: { x: 0.5, y: 0.5 },
      size: { x: '30px', y: '30px' },
    },
    init: async ({ assetName }) => {
      const sprite = pixi.Sprite.from(assetName);
      return sprite;
    },
  },
  ...getSlotDisplayObjects({
    name: 'BIG',
    choice: BetChoiceEnum.BIG,
    titleKey: 'sic_bo_big',
    rate: 2,
    subtitleKey: 'sic_bo_big_detail',
    position: { x: '4px', y: '46px' },
    size: { x: 144, y: 44 },
    betsPosition: { x: '88px', y: '-20px' },
  }),
  ...getSlotDisplayObjects({
    name: 'SMALL',
    choice: BetChoiceEnum.SMALL,
    titleKey: 'sic_bo_small',
    rate: 2,
    subtitleKey: 'sic_bo_small_detail',
    position: { x: '-148px', y: '46px' },
    size: { x: 144, y: 44 },
    betsPosition: { x: '64px', y: '-20px' },
  }),
  ...getSlotDisplayObjects({
    name: 'TRIPLES',
    choice: BetChoiceEnum.TRIPLES,
    titleKey: 'sic_bo_any_triple',
    rate: 20,
    subtitleKey: 'sic_bo_any_triple_detail',
    position: { x: '-148px', y: '98px' },
    size: { x: 296, y: 44 },
    betsPosition: { x: '148px', y: '54px' },
  }),
  [DisplayObjectNames.USER_POINTS]: {
    name: DisplayObjectNames.USER_POINTS,
    geometry: {
      position: { x: 0.5, y: 1 },
    },
    init: async () => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.yellow, 0);
      graphic.drawRect(-174, -28, 152, 24);
      graphic.endFill();

      graphic.eventMode = 'static';
      graphic.cursor = 'pointer';
      graphic.on('pointertap', () => {
        const gameCore = getGameCore();
        gameCore.eventTarget?.addvalue();
      });

      return graphic;
    },
  },
  [DisplayObjectNames.USER_POINTS_SPRITE]: {
    name: DisplayObjectNames.USER_POINTS_SPRITE,
    geometry: {
      anchor: { x: 0, y: 0.5 },
      position: { x: '-174px', y: '-18px' },
      size: { x: '152px', y: '24px' },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(DisplayObjectNames.USER_POINTS_SPRITE);
      return sprite;
    },
  },
  [DisplayObjectNames.USER_POINTS_DIAMOND_SPRITE]: {
    name: DisplayObjectNames.USER_POINTS_DIAMOND_SPRITE,
    geometry: {
      anchor: { x: 0, y: 0.5 },
      position: { x: '-164px', y: '-18px' },
      size: { x: '12px', y: '12px' },
    },
    init: async () => {
      const sprite = pixi.Sprite.from(
        DisplayObjectNames.USER_POINTS_DIAMOND_SPRITE
      );
      return sprite;
    },
  },
  [DisplayObjectNames.USER_POINTS_NUMBER]: {
    name: DisplayObjectNames.USER_POINTS_NUMBER,
    geometry: {
      position: { x: '-150px', y: '-18px' },
      anchor: { x: 0, y: 0.5 },
    },
    init: async ({ gameCore }) => {
      const style = new pixi.TextStyle({
        fill: color.tealBlue,
        fontSize: 12,
      });
      const text = new pixi.Text('-', style);
      if (!isNaN(gameCore?.user?.points)) {
        text.text = numberWithCommas({ number: gameCore.user.points });
      }
      return text;
    },
  },
  [DisplayObjectNames.CHIPS_SELECTOR]: {
    name: DisplayObjectNames.CHIPS_SELECTOR,
    geometry: {
      position: { x: 0.5, y: 1 },
    },
    init: async () => {
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.salmon, 0);
      graphic.drawRect(-2, -34, 168, 30);
      graphic.endFill();
      return graphic;
    },
  },
  ...getChipSelectorDisplayObjects({
    name: '100',
    position: { x: '28px', y: '-4px' },
    amount: 100,
  }),
  ...getChipSelectorDisplayObjects({
    name: '1000',
    position: { x: '66px', y: '-4px' },
    amount: 1000,
  }),
  ...getChipSelectorDisplayObjects({
    name: '10K',
    position: { x: '104px', y: '-4px' },
    amount: 10000,
  }),
  ...getChipSelectorDisplayObjects({
    name: '100K',
    position: { x: '142px', y: '-4px' },
    amount: 100000,
  }),
  [DisplayObjectNames.ROLL_VIDEO_TOASTER]: {
    name: DisplayObjectNames.ROLL_VIDEO_TOASTER,
    geometry: {
      position: { x: 0.5, y: '150px' },
    },
    init: async ({ gameCore }) => {
      if (!gameCore?.app?.screen) {
        return;
      }
      const { width, height } = gameCore.app.screen;
      const graphic = new pixi.Graphics();
      graphic.beginFill(color.salmon, 0);
      graphic.drawRect(-width / 2, -(height - 36) / 2, width, height - 36);
      graphic.endFill();
      return graphic;
    },
  },
  [DisplayObjectNames.ROLL_VIDEO_START_SPRITE]: getRollVideoSpriteDisplayObject(
    {
      name: DisplayObjectNames.ROLL_VIDEO_START_SPRITE,
      videoDomName: DomResourceNames.ROLL_VIDEO_START,
    }
  ),
  [DisplayObjectNames.ROLL_VIDEO_MIDDLE_SPRITE]:
    getRollVideoSpriteDisplayObject({
      name: DisplayObjectNames.ROLL_VIDEO_MIDDLE_SPRITE,
      videoDomName: DomResourceNames.ROLL_VIDEO_MIDDLE,
    }),
  [DisplayObjectNames.ROLL_VIDEO_END_SPRITE]: getRollVideoSpriteDisplayObject({
    name: DisplayObjectNames.ROLL_VIDEO_END_SPRITE,
    videoDomName: DomResourceNames.ROLL_VIDEO_END,
  }),
};

const eventTarget = new EventTarget();
eventTarget.bet = function ({ choice, amount }) {
  const event = new CustomEvent('bet', {
    detail: { choice, amount },
  });
  this.dispatchEvent(event);
};
eventTarget.chooseChip = function ({ amount }) {
  const event = new CustomEvent('choosechip', { detail: { amount } });
  this.dispatchEvent(event);
};
eventTarget.addvalue = function () {
  const event = new CustomEvent('addvalue');
  this.dispatchEvent(event);
};
eventTarget.emitVideoEvent = function ({ name, type, video }) {
  const event = new CustomEvent(type, { detail: { name, video } });
  this.dispatchEvent(event);
};
eventTarget.emitTweenEvent = function ({ name, type, target }) {
  const event = new CustomEvent(type, { detail: { name, target } });
  this.dispatchEvent(event);
};
eventTarget.viewOpen = function ({ name, target, extraProps }) {
  const event = new CustomEvent('viewopen', {
    detail: { name, target, extraProps },
  });
  this.dispatchEvent(event);
};

const gameCore = {
  app: null,
  wrapper: null,
  currentGame: {
    id: null,
    gameStatus: GameStateEnum.INITING_GAME,
    ntpTimeOffset: 0,
    stateStartTimestamp: null,
    stateEndTimestamp: null,
    bets: [],
    rollResults: [],
    odds: {},
  },
  user: {
    points: null,
    bets: [
      // { choice: BetChoiceEnum.BIG, amount: 100, timestamp: 0 }, // sample bet
    ],
    activeChip: null,
    hoverChip: null,
  },
  scene: {
    name: GameStateEnum.INITING_GAME,
    ntpTimeOffset: 0,
    startTimestamp: null,
    endTimestamp: null,
    eventListeners: [],
  },
  currentTick: null,
  containers: {
    [DisplayObjectNames.BACKGROUND_CONTAINER]: null,
    [DisplayObjectNames.TABLE_CONTAINER]: null,
    [DisplayObjectNames.FOREGROUND_CONTAINER]: null,
    [DisplayObjectNames.UI_CONTAINER]: null,
    [DisplayObjectNames.TOASTER_CONTAINER]: null,
    [DisplayObjectNames.MODAL_CONTAINER]: null,
  },
  displayObjects: {},
  dynamicDisplayObjects: {
    [DisplayObjectNames.BET_TOASTERS]: {
      name: DisplayObjectNames.BET_TOASTERS,
      children: [],
    },
    [DisplayObjectNames.BETTED_CHIPS]: {
      name: DisplayObjectNames.BETTED_CHIPS,
      children: [],
    },
  },
  domResources: {},
  eventTarget,
};

const createBetWinResultDomResource = () => {
  const diamondStyle = 'width: 24px; height: 24px; vertical-align: middle;';
  const numberStyle = `color: ${color.tealBlue}; margin-left: 4px`;
  const elementDefines = [
    {
      name: DomResourceNames.BET_RESULT_WIN_CONTENT,
      type: 'span',
      // TODO: remove padding right hacking for canvas issue
      style:
        'position: absolute; top: 0px; font-size: 24px; line-height: 24px; padding-right: 4px;',
    },
    {
      name: DomResourceNames.BET_RESULT_WIN_DIAMOND,
      type: 'img',
      style: diamondStyle,
      src: Diamond24Image,
    },
    {
      name: DomResourceNames.BET_RESULT_WIN_NUMBER,
      type: 'span',
      style: numberStyle,
      textContent: '-',
    },
  ];

  const [content, ...children] = elementDefines.map(define => {
    const element = document.createElement(define.type);
    element.className = define.name;
    element.style = define.style;
    element.textContent = define.textContent;
    element.src = define.src;
    return element;
  });
  children.forEach(element => content.appendChild(element));

  return content;
};

const createBetResultDomResource = ({
  inputTextContent,
  outputTextContent,
}) => {
  const diamondStyle =
    'width: 12px; height: 12px; margin-left: 8px; vertical-align: top;';
  // need inline-block to make height to 12px
  const numberStyle = `display: inline-block; color: ${color.tealBlue}; margin-left: 2px`;
  const textStyle = 'display: inline-block';
  const elementDefines = [
    {
      name: DomResourceNames.BET_RESULT_SUMMARY_CONTENT,
      type: 'span',
      style: `position: absolute; top: 0px; display: inline-block; font-size: 12px; color: ${color.white}; line-height: 12px;`,
    },
    {
      name: DomResourceNames.BET_RESULT_SUMMARY_INPUT_TEXT,
      type: 'span',
      style: textStyle,
      textContent: inputTextContent,
    },
    {
      name: DomResourceNames.BET_RESULT_SUMMARY_INPUT_DIAMOND,
      type: 'img',
      style: diamondStyle,
      src: Diamond12Image,
    },
    {
      name: DomResourceNames.BET_RESULT_SUMMARY_INPUT_NUMBER,
      type: 'span',
      style: numberStyle,
      textContent: '-',
    },
    {
      name: DomResourceNames.BET_RESULT_SUMMARY_OUTPUT_TEXT,
      type: 'span',
      style: `margin-left: 8px; ${textStyle}`,
      textContent: outputTextContent,
    },
    {
      name: DomResourceNames.BET_RESULT_SUMMARY_OUTPUT_DIAMOND,
      type: 'img',
      style: diamondStyle,
      src: Diamond12Image,
    },
    {
      name: DomResourceNames.BET_RESULT_SUMMARY_OUTPUT_NUMBER,
      type: 'span',
      style: numberStyle,
      textContent: '-',
    },
  ];

  const [content, ...children] = elementDefines.map(define => {
    const element = document.createElement(define.type);
    element.className = define.name;
    element.style = define.style;
    element.textContent = define.textContent;
    element.src = define.src;
    return element;
  });
  children.forEach(element => content.appendChild(element));

  return content;
};

const createRollVideoDomResource = async ({ gameCore }) => {
  // need to use async import to prevent loadable-component error
  const [
    { default: SicBoRollStartVideoSource },
    { default: SicBoRollMiddleVideoSource },
    { default: SicBoRollEndVideoSource },
  ] = await Promise.all([
    import('../../video/SicBo_Animation_Start_720p.mp4'),
    import('../../video/SicBo_Animation_Middle_720p.mp4'),
    import('../../video/SicBo_Animation_End_720p.mp4'),
  ]);
  const style = 'width: 1px; height: 1px; opacity: 0; overflow: hidden;';
  const videoProps = {
    muted: true,
    preload: 'metadata',
    playsInline: true,
  };
  const elementDefines = [
    {
      name: DomResourceNames.ROLL_VIDEOS,
      type: 'div',
      props: { style },
    },
    {
      name: DomResourceNames.ROLL_VIDEO_START,
      type: 'video',
      props: { src: SicBoRollStartVideoSource + '?lang=en', ...videoProps },
    },
    {
      name: DomResourceNames.ROLL_VIDEO_MIDDLE,
      type: 'video',
      props: {
        src: SicBoRollMiddleVideoSource + '?lang=en',
        ...videoProps,
      },
    },
    {
      name: DomResourceNames.ROLL_VIDEO_END,
      type: 'video',
      props: { src: SicBoRollEndVideoSource + '?lang=en', ...videoProps },
    },
  ];

  const [content, start, middle, end] = elementDefines.map(define => {
    const element = document.createElement(define.type);
    element.className = define.name;
    Object.keys(define.props).forEach(key => {
      element[key] = define.props[key];
    });
    return element;
  });
  [start, middle, end].forEach(element => {
    element.addEventListener('ended', () => {
      gameCore?.eventTarget?.emitVideoEvent({
        name: element.className,
        type: 'videoEnded',
        video: element,
      });
    });
    return content.appendChild(element);
  });

  return content;
};

export const initGame = async () => {
  const log = gameDebugLog.extend('initGame');
  const errorLog = gameError.extend('initGame');
  try {
    log('start init sic-bo game');
    if (!pixi) {
      await loadPixiJSPromise();
      if (!pixi) {
        throw new Error('missing pixi lib');
      }
    }
    if (!gameCore.wrapper) {
      gameCore.wrapper = document.createElement('div');
    }
    if (!gameCore.app) {
      gameCore.app = new pixi.Application({
        resizeTo: gameCore.wrapper,
        backgroundAlpha: 0,
        autoDensity: true,
        resolution: Math.max(window.devicePixelRatio || 2, 2),
      });
      gameCore.app.ticker.stop();
      gsap.ticker.add(() => {
        gameCore.app.ticker.update();
      });
      gameCore.wrapper.appendChild(gameCore.app.view);
      log('start init app', gameCore.app);
      window._gameCore_ = gameCore;

      gameCore.app.stage.name = DisplayObjectNames.ROOT;
      Object.keys(gameCore.containers).forEach(key => {
        gameCore.containers[key] = new pixi.Container();
        gameCore.containers[key].name = key;
        gameCore.displayObjects[key] = gameCore.containers[key];
        gameCore.app.stage.addChild(gameCore.containers[key]);
      });

      pixi.Assets.init({ manifest });
      Object.keys(GameStateEnum).forEach(stateKey => {
        pixi.Assets.backgroundLoadBundle(GameStateEnum[stateKey]);
        log('background load bundle', GameStateEnum[stateKey]);
      });

      const hiddenContainer = document.createElement('div');
      hiddenContainer.id = DomResourceNames.HIDDEN_CONTAINER;
      hiddenContainer.style =
        'position: fixed; top: 0px; left: 0px; opacity: 0; height: 1px; overflow: hidden;';
      gameCore.domResources[DomResourceNames.HIDDEN_CONTAINER] =
        hiddenContainer;
      gameCore.wrapper.appendChild(hiddenContainer);

      const betWinResultContent = createBetWinResultDomResource();
      gameCore.domResources[DomResourceNames.BET_RESULT_WIN_CONTENT] =
        betWinResultContent;
      log('create bet win result content', betWinResultContent);

      const betResultContent = createBetResultDomResource({
        inputTextContent: i18n.t('sic_bo_your_bet', {
          ns: TranslationNamespace.LIVESTREAM,
        }),
        outputTextContent: i18n.t('sic_bo_your_win', {
          ns: TranslationNamespace.LIVESTREAM,
        }),
      });
      gameCore.domResources[DomResourceNames.BET_RESULT_SUMMARY_CONTENT] =
        betResultContent;
      log('create bet result summary content', betResultContent);

      createRollVideoDomResource({ gameCore }).then(rollVideoContent => {
        hiddenContainer.appendChild(rollVideoContent);
        gameCore.domResources[DomResourceNames.ROLL_VIDEOS] = rollVideoContent;
        log('create roll video content', rollVideoContent);
      });

      // setup custom gsap animations
      gsap.registerEffect({
        name: 'flip',
        effect: (targets, { width, height, ...config }) => {
          return gsap.to(targets, {
            keyframes: {
              '0%': { width, height: 0, alpha: 0 },
              '20%': { height, alpha: 1 },
              '90%': { width, height, alpha: 1 },
              '100%': { width: 0, height: 0, alpha: 0 },
            },
            ...config,
          });
        },
        defaults: { duration: TOASTER_DURATION_MSEC / 1000 },
        extendTimeline: true,
      });
      gsap.registerEffect({
        name: 'fadeInOut',
        effect: (targets, config) => {
          return gsap.to(targets, {
            keyframes: {
              '0%': { alpha: 0 },
              '20%': { alpha: 1 },
              '90%': { alpha: 1 },
              '100%': { alpha: 0 },
            },
            ...config,
          });
        },
        defaults: { duration: TOASTER_DURATION_MSEC / 1000 },
        extendTimeline: true,
      });

      log('done init sic-bo game', gameCore.app);
    }
  } catch (error) {
    errorLog('init sic-bo game error', error);
  }
  return gameCore;
};

const BackgroundRenderTree = {
  name: DisplayObjectNames.BACKGROUND_CONTAINER,
  children: [
    {
      name: DisplayObjectNames.BACKGROUND_SPRITE,
    },
    {
      name: DisplayObjectNames.COUNTDOWN,
    },
  ],
};
const TableRenderTree = {
  name: DisplayObjectNames.TABLE_CONTAINER,
  children: [
    {
      name: DisplayObjectNames.TABLE,
      children: [
        {
          name: DisplayObjectNames.TABLE_SPRITE,
        },
        {
          name: DisplayObjectNames.SMALL_SLOT,
          children: [
            {
              name: DisplayObjectNames.SMALL_SLOT_TITLE,
            },
            {
              name: DisplayObjectNames.SMALL_SLOT_SUBTITLE,
            },
            {
              name: DisplayObjectNames.SMALL_BETS,
              children: [
                {
                  name: DisplayObjectNames.SMALL_BETS_NUMBER,
                },
              ],
            },
          ],
        },
        {
          name: DisplayObjectNames.BIG_SLOT,
          children: [
            {
              name: DisplayObjectNames.BIG_SLOT_TITLE,
            },
            {
              name: DisplayObjectNames.BIG_SLOT_SUBTITLE,
            },
            {
              name: DisplayObjectNames.BIG_BETS,
              children: [
                {
                  name: DisplayObjectNames.BIG_BETS_NUMBER,
                },
              ],
            },
          ],
        },
        {
          name: DisplayObjectNames.TRIPLES_SLOT,
          children: [
            {
              name: DisplayObjectNames.TRIPLES_SLOT_TITLE,
            },
            {
              name: DisplayObjectNames.TRIPLES_SLOT_SUBTITLE,
            },
            {
              name: DisplayObjectNames.TRIPLES_BETS,
              children: [
                {
                  name: DisplayObjectNames.TRIPLES_BETS_NUMBER,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      name: DisplayObjectNames.DEALER_SPRITE,
    },
  ],
};
const ForegroundRenderTree = {
  name: DisplayObjectNames.FOREGROUND_CONTAINER,
  children: [
    {
      name: DisplayObjectNames.BETTED_CHIPS,
      ignoreChildren: true,
    },
  ],
};
const UiRenderTree = {
  name: DisplayObjectNames.UI_CONTAINER,
  children: [
    {
      name: DisplayObjectNames.HEADER,
    },
    {
      name: DisplayObjectNames.SCENE_TITLE,
    },
    {
      name: DisplayObjectNames.USER_POINTS,
      children: [
        {
          name: DisplayObjectNames.USER_POINTS_SPRITE,
        },
        {
          name: DisplayObjectNames.USER_POINTS_DIAMOND_SPRITE,
        },
        {
          name: DisplayObjectNames.USER_POINTS_NUMBER,
        },
      ],
    },
    {
      name: DisplayObjectNames.CHIPS_SELECTOR,
      children: [
        {
          name: DisplayObjectNames.CHIPS_SELECTOR_100,
          children: [
            {
              name: DisplayObjectNames.CHIPS_SELECTOR_100_SPRITE,
            },
            {
              name: DisplayObjectNames.CHIPS_SELECTOR_100_HALO,
            },
          ],
        },
        {
          name: DisplayObjectNames.CHIPS_SELECTOR_1000,
          children: [
            {
              name: DisplayObjectNames.CHIPS_SELECTOR_1000_SPRITE,
            },
            {
              name: DisplayObjectNames.CHIPS_SELECTOR_1000_HALO,
            },
          ],
        },
        {
          name: DisplayObjectNames.CHIPS_SELECTOR_10K,
          children: [
            {
              name: DisplayObjectNames.CHIPS_SELECTOR_10K_SPRITE,
            },
            {
              name: DisplayObjectNames.CHIPS_SELECTOR_10K_HALO,
            },
          ],
        },
        {
          name: DisplayObjectNames.CHIPS_SELECTOR_100K,
          children: [
            {
              name: DisplayObjectNames.CHIPS_SELECTOR_100K_SPRITE,
            },
            {
              name: DisplayObjectNames.CHIPS_SELECTOR_100K_HALO,
            },
          ],
        },
      ],
    },
  ],
};
const RollVideoRenderTree = {
  name: DisplayObjectNames.ROLL_VIDEO_TOASTER,
  children: [
    {
      name: DisplayObjectNames.ROLL_VIDEO_START_SPRITE,
    },
    {
      name: DisplayObjectNames.ROLL_VIDEO_MIDDLE_SPRITE,
    },
    {
      name: DisplayObjectNames.ROLL_VIDEO_END_SPRITE,
    },
  ],
};
const ToasterRenderTree = {
  name: DisplayObjectNames.TOASTER_CONTAINER,
  children: [
    {
      name: DisplayObjectNames.TOASTER_BACKGROUND,
    },
    {
      name: DisplayObjectNames.BET_TOASTERS,
      ignoreChildren: true,
    },
    {
      name: DisplayObjectNames.GAME_STATUS_TOASTER,
      children: [
        {
          name: DisplayObjectNames.GAME_STATUS_TOASTER_SPRITE,
        },
        {
          name: DisplayObjectNames.GAME_STATUS_TOASTER_TITLE,
        },
      ],
    },
  ],
};
const RollingToasterRenderTree = {
  name: DisplayObjectNames.TOASTER_CONTAINER,
  children: [...ToasterRenderTree.children, RollVideoRenderTree],
};
const DrawingToasterRenderTree = {
  name: DisplayObjectNames.TOASTER_CONTAINER,
  children: [
    ...ToasterRenderTree.children,
    {
      name: DisplayObjectNames.DRAW_RESULT_TOASTER,
      children: [
        {
          name: DisplayObjectNames.DRAW_RESULT_DICE_1ST,
        },
        {
          name: DisplayObjectNames.DRAW_RESULT_DICE_2ND,
        },
        {
          name: DisplayObjectNames.DRAW_RESULT_DICE_3RD,
        },
        {
          name: DisplayObjectNames.DRAW_RESULT_CONTENT,
          children: [
            {
              name: DisplayObjectNames.DRAW_RESULT_CONTENT_SPRITE,
            },
            {
              name: DisplayObjectNames.DRAW_RESULT_CONTENT_TEXT,
            },
          ],
        },
      ],
    },
    {
      name: DisplayObjectNames.BET_RESULT_TOASTER,
      children: [
        {
          name: DisplayObjectNames.BET_RESULT_WIN_TOASTER,
          children: [
            {
              name: DisplayObjectNames.BET_RESULT_WIN_SPRITE,
            },
          ],
        },
      ],
    },
    RollVideoRenderTree,
  ],
};

/*
const step = ({ min, max, value }) => {
  return Math.max(Math.min((value - min) / (max - min), 1), 0);
};
*/

const chipsTick = ({ gameCore }) => {
  if (!gameCore?.user) {
    return;
  }
  const { hoverChip, activeChip } = gameCore.user;
  [
    { name: 'CHIPS_SELECTOR_100', amount: 100 },
    { name: 'CHIPS_SELECTOR_1000', amount: 1000 },
    { name: 'CHIPS_SELECTOR_10K', amount: 10000 },
    { name: 'CHIPS_SELECTOR_100K', amount: 100000 },
  ].forEach(({ name, amount }) => {
    const spriteName = `${name}_SPRITE`;
    const haloName = `${name}_HALO`;

    const halo = gameCore.displayObjects[DisplayObjectNames[haloName]];
    if (halo) {
      halo.visible = hoverChip === amount || activeChip === amount;
    }

    const positionY = amount === gameCore.user.activeChip ? -23 : -15;
    const sprite = gameCore.displayObjects[DisplayObjectNames[spriteName]];
    if (sprite && positionY !== sprite.position.y) {
      sprite.position.y = positionY;
      halo.position.y = positionY;
    }
  });
};

const Scenes = {
  [GameStateEnum.WAITING_GAME_ID]: {
    name: GameStateEnum.WAITING_GAME_ID,
    renderTree: {
      name: DisplayObjectNames.ROOT,
      children: [
        BackgroundRenderTree,
        TableRenderTree,
        ForegroundRenderTree,
        UiRenderTree,
        // ToasterRenderTree,
        // RollingToasterRenderTree,
        // DrawingToasterRenderTree,
      ],
    },
    init: async () => {
      await pixi.Assets.loadBundle(GameStateEnum.WAITING_GAME_ID);
      // await pixi.Assets.loadBundle(GameStateEnum.ROLLING);
    },
    onTreeSynced: async ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      if (sceneTitle && sceneTitle.text !== GameStateEnum.WAITING_GAME_ID) {
        sceneTitle.text = GameStateEnum.WAITING_GAME_ID;
      }
    },
    tick:
      ({ gameCore }) =>
      () => {
        chipsTick({ gameCore });
      },
    exit: ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      sceneTitle.text = '';
    },
  },
  [GameStateEnum.PREPARING]: {
    name: GameStateEnum.PREPARING,
    renderTree: {
      name: DisplayObjectNames.ROOT,
      children: [
        BackgroundRenderTree,
        TableRenderTree,
        UiRenderTree,
        ToasterRenderTree,
      ],
    },
    init: async ({ gameCore }) => {
      await pixi.Assets.loadBundle(GameStateEnum.PREPARING);

      const countdown = gameCore.displayObjects[DisplayObjectNames.COUNTDOWN];
      if (countdown) {
        countdown.text = '00';
      }
    },
    onTreeSynced: async ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      if (sceneTitle && sceneTitle.text !== GameStateEnum.PREPARING) {
        sceneTitle.text = GameStateEnum.PREPARING;
      }

      [
        {
          choice: BetChoiceEnum.SMALL,
          name: DisplayObjectNames.SMALL_SLOT_TITLE,
        },
        {
          choice: BetChoiceEnum.BIG,
          name: DisplayObjectNames.BIG_SLOT_TITLE,
        },
        {
          choice: BetChoiceEnum.TRIPLES,
          name: DisplayObjectNames.TRIPLES_SLOT_TITLE,
        },
      ].forEach(({ choice, name }) => {
        const object = gameCore.displayObjects[name];
        object.text = object.text.replace(
          /[^ ]+X/,
          `${gameCore.currentGame.odds?.[choice] || '?'}X`
        );
      });

      const titleText = i18n.t('sic_bo_start', {
        ns: TranslationNamespace.LIVESTREAM,
      });
      const toasterTitle =
        gameCore.displayObjects[DisplayObjectNames.GAME_STATUS_TOASTER_TITLE];
      if (toasterTitle && toasterTitle.text !== titleText) {
        toasterTitle.text = titleText;
      }
      const toaster =
        gameCore.displayObjects[DisplayObjectNames.GAME_STATUS_TOASTER];
      if (toaster) {
        toaster.visible = true;
        toaster.tween?.restart();
      }
      const toasterBackground =
        gameCore.displayObjects[DisplayObjectNames.TOASTER_BACKGROUND];
      if (toasterBackground) {
        toasterBackground.visible = true;
        toasterBackground.tween?.restart();
      }
    },
    tick:
      ({ gameCore }) =>
      () => {
        chipsTick({ gameCore });
      },
    exit: ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      sceneTitle.text = '';
    },
  },
  [GameStateEnum.BETTING]: {
    name: GameStateEnum.BETTING,
    renderTree: {
      name: DisplayObjectNames.ROOT,
      children: [
        BackgroundRenderTree,
        TableRenderTree,
        ForegroundRenderTree,
        UiRenderTree,
        ToasterRenderTree,
      ],
    },
    init: async ({ gameCore }) => {
      await pixi.Assets.loadBundle(GameStateEnum.BETTING);
      pixi.Assets.loadBundle(GameStateEnum.ROLLING);

      [
        DisplayObjectNames.BIG_SLOT,
        DisplayObjectNames.SMALL_SLOT,
        DisplayObjectNames.TRIPLES_SLOT,
      ].forEach(slotName => {
        const slot = gameCore.displayObjects[slotName];
        if (slot) {
          slot.eventMode = 'static';
          slot.cursor = 'pointer';
        }
      });
    },
    onTreeSynced: async ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      if (sceneTitle && sceneTitle.text !== GameStateEnum.BETTING) {
        sceneTitle.text = GameStateEnum.BETTING;
      }
    },
    tick:
      ({ gameCore }) =>
      () => {
        // update betting countdown number
        const countdown = gameCore.displayObjects[DisplayObjectNames.COUNTDOWN];
        if (countdown) {
          const endTime = gameCore.scene.endTimestamp;
          const ntpTimeOffset = gameCore.scene.ntpTimeOffset;
          if (!isNaN(endTime) && !isNaN(ntpTimeOffset)) {
            const remainTime = endTime - (Date.now() + ntpTimeOffset);
            const newText = `${Math.ceil(remainTime / 1000)}`.padStart(2, '0');
            if (countdown.text !== newText) {
              countdown.text = newText;
            }
          }
        }

        // update bet numbers
        [
          {
            objectName: DisplayObjectNames.BIG_BETS_NUMBER,
            choice: BetChoiceEnum.BIG,
          },
          {
            objectName: DisplayObjectNames.SMALL_BETS_NUMBER,
            choice: BetChoiceEnum.SMALL,
          },
          {
            objectName: DisplayObjectNames.TRIPLES_BETS_NUMBER,
            choice: BetChoiceEnum.TRIPLES,
          },
        ].forEach(({ objectName, choice }) => {
          const numberObject = gameCore.displayObjects[objectName];
          if (numberObject) {
            const number = gameCore.user.bets
              .filter(bet => choice === bet.choice)
              .reduce((acc, curr) => {
                return acc + curr.amount;
              }, 0);
            const text =
              number >= 1000000
                ? `${Math.floor(number / 100000) / 10}M`
                : number >= 10000
                  ? `${Math.floor(number / 1000)}K`
                  : `${number}`;
            if (numberObject.text !== text) {
              numberObject.text = text;
            }
          }
        });

        chipsTick({ gameCore });
      },
    exit: ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      if (sceneTitle) {
        sceneTitle.text = '';
      }
      const countdown = gameCore.displayObjects[DisplayObjectNames.COUNTDOWN];
      if (countdown) {
        countdown.text = '00';
      }

      [
        DisplayObjectNames.BIG_SLOT,
        DisplayObjectNames.SMALL_SLOT,
        DisplayObjectNames.TRIPLES_SLOT,
      ].forEach(slotName => {
        const slot = gameCore.displayObjects[slotName];
        if (slot) {
          slot.eventMode = 'auto';
          slot.cursor = 'not-allowed';
        }
      });
    },
  },
  [GameStateEnum.ROLLING]: {
    name: GameStateEnum.ROLLING,
    renderTree: {
      name: DisplayObjectNames.ROOT,
      children: [
        BackgroundRenderTree,
        TableRenderTree,
        ForegroundRenderTree,
        UiRenderTree,
        RollingToasterRenderTree,
      ],
    },
    init: async () => {
      await pixi.Assets.loadBundle(GameStateEnum.ROLLING);
      pixi.Assets.loadBundle(GameStateEnum.DRAWING);
    },
    onTreeSynced: async ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      if (sceneTitle && sceneTitle.text !== GameStateEnum.ROLLING) {
        sceneTitle.text = GameStateEnum.ROLLING;
      }

      const countdown = gameCore.displayObjects[DisplayObjectNames.COUNTDOWN];
      if (countdown) {
        countdown.text = '00';
      }

      const titleText = i18n.t('sic_bo_stop', {
        ns: TranslationNamespace.LIVESTREAM,
      });
      const toasterTitle =
        gameCore.displayObjects[DisplayObjectNames.GAME_STATUS_TOASTER_TITLE];
      if (toasterTitle && toasterTitle.text !== titleText) {
        toasterTitle.text = titleText;
      }
      const toaster =
        gameCore.displayObjects[DisplayObjectNames.GAME_STATUS_TOASTER];
      if (toaster) {
        toaster.visible = true;
        toaster.tween?.restart();
      }
      const toasterBackground =
        gameCore.displayObjects[DisplayObjectNames.TOASTER_BACKGROUND];
      if (toasterBackground) {
        toasterBackground.visible = true;
        toasterBackground.tween?.restart();
      }

      const handleStatusToasterEnded = async event => {
        const rollStartVideo =
          gameCore.displayObjects[DisplayObjectNames.ROLL_VIDEO_START_SPRITE];
        if (!rollStartVideo) {
          return;
        }
        rollStartVideo.visible = true;
        try {
          const startVideo = rollStartVideo.texture.baseTexture.resource.source;
          startVideo.currentTime = 0;
          await startVideo.play();
        } catch (error) {
          gameError('play roll start video fail', { error, event, gameCore });
        }
      };
      const handleVideoEnded = async event => {
        if (DomResourceNames.ROLL_VIDEO_START !== event.detail?.name) {
          return;
        }

        const rollMiddleSprite =
          gameCore.displayObjects[DisplayObjectNames.ROLL_VIDEO_MIDDLE_SPRITE];
        if (!rollMiddleSprite) {
          return;
        }
        rollMiddleSprite.visible = true;
        try {
          const middleVideo =
            rollMiddleSprite.texture.baseTexture.resource.source;
          middleVideo.currentTime = 0;
          middleVideo.loop = true;
          await middleVideo.play();
        } catch (error) {
          gameError('play roll middle video fail', { error, event, gameCore });
        }

        const rollStartSprite =
          gameCore.displayObjects[DisplayObjectNames.ROLL_VIDEO_START_SPRITE];
        if (!rollStartSprite) {
          return;
        }
        rollStartSprite.visible = false;
        const rollStartVideo =
          rollStartSprite.texture.baseTexture.resource?.source;
        if (rollStartVideo) {
          rollStartVideo.currentTime = 0;
        }
      };

      [
        { type: 'tweenComplete', listener: handleStatusToasterEnded },
        { type: 'videoEnded', listener: handleVideoEnded },
      ].forEach(({ type, listener }) => {
        gameCore.eventTarget.addEventListener(type, listener);
        gameCore.scene.eventListeners.push({ type, listener });
      });
    },
    tick:
      ({ gameCore }) =>
      () => {
        chipsTick({ gameCore });
      },
    exit: ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      if (sceneTitle) {
        sceneTitle.text = '';
      }
    },
  },
  [GameStateEnum.DRAWING]: {
    name: GameStateEnum.DRAWING,
    renderTree: {
      name: DisplayObjectNames.ROOT,
      children: [
        BackgroundRenderTree,
        TableRenderTree,
        ForegroundRenderTree,
        UiRenderTree,
        DrawingToasterRenderTree,
      ],
    },
    init: async () => {
      await pixi.Assets.loadBundle(GameStateEnum.DRAWING);
    },
    onTreeSynced: async ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      if (sceneTitle && sceneTitle.text !== GameStateEnum.DRAWING) {
        sceneTitle.text = GameStateEnum.DRAWING;
      }

      const rollResults = gameCore.currentGame?.rollResults;
      if (!rollResults?.length) {
        return;
      }

      // compute draw data
      const diceTotal = rollResults.reduce((c, v) => c + v, 0);
      const isTriples =
        rollResults[0] === rollResults[1] && rollResults[0] === rollResults[2];
      const odd = isTriples
        ? BetChoiceEnum.TRIPLES
        : diceTotal >= 11
          ? BetChoiceEnum.BIG
          : BetChoiceEnum.SMALL;
      const betTotal = gameCore.user.bets.reduce((acc, curr) => {
        return acc + curr.amount;
      }, 0);
      const winInputPoints = gameCore.user.bets
        .filter(bet => odd === bet.choice)
        .reduce((acc, curr) => {
          return acc + curr.amount;
        }, 0);
      const winRate = gameCore.currentGame?.odds[odd];
      let earnedPoints = 0;
      if (winInputPoints && winRate) {
        earnedPoints = winInputPoints * winRate;
      }
      let summaryToaster = null;

      // stop looping middle video
      const middleVideo =
        gameCore.displayObjects[DisplayObjectNames.ROLL_VIDEO_MIDDLE_SPRITE]
          ?.texture.baseTexture.resource?.source;
      if (middleVideo) {
        middleVideo.loop = false;
      }

      // update draw result toaster
      const updateDrawResultToaster = async () => {
        [
          DisplayObjectNames.DRAW_RESULT_DICE_1ST,
          DisplayObjectNames.DRAW_RESULT_DICE_2ND,
          DisplayObjectNames.DRAW_RESULT_DICE_3RD,
        ].forEach((name, index) => {
          const sprite = gameCore.displayObjects[name];
          const diceValue = gameCore.currentGame?.rollResults[index];
          if (!diceValue) {
            return;
          }
          sprite.texture = pixi.Texture.from(DiceNameMap[diceValue]);
        });
        const rollResultText = `${diceTotal}  ${i18n.t(BetTextKeyMap[odd], {
          ns: TranslationNamespace.LIVESTREAM,
        })}`;
        const textObject =
          gameCore.displayObjects[DisplayObjectNames.DRAW_RESULT_CONTENT_TEXT];
        textObject.text = rollResultText;
      };

      // Congratulations
      const updateBetResultWinToaster = async () => {
        if (earnedPoints) {
          const winContentDefine =
            displayObjects[DisplayObjectNames.BET_RESULT_WIN_CONTENT_SPRITE];
          const winContentSprite = await winContentDefine?.init({
            gameCore,
            earnedPoints,
          });
          if (winContentSprite) {
            const geometry = winContentDefine?.geometry;
            winContentSprite.position.set(geometry.position.x, 10);
            winContentSprite.anchor.set(geometry.anchor.x, geometry.anchor.y);
          }
          const winToaster =
            gameCore.displayObjects[DisplayObjectNames.BET_RESULT_WIN_TOASTER];
          if (winToaster) {
            winToaster.addChild(winContentSprite);
          }
        }
      };

      // `投入 1000 獲得 2000`
      const createBetSummary = async () => {
        const summarySpriteDefine =
          displayObjects[DisplayObjectNames.BET_RESULT_SUMMARY_CONTENT_SPRITE];
        const summarySprite = await summarySpriteDefine?.init({
          gameCore,
          inputNumber: betTotal,
          outputNumber: earnedPoints,
        });
        if (!summarySprite) {
          return;
        }
        summarySprite.name =
          DisplayObjectNames.BET_RESULT_SUMMARY_CONTENT_SPRITE;
        const geometry = summarySpriteDefine.geometry;
        summarySprite.anchor.set(geometry.anchor.x, geometry.anchor.y);

        const summaryDefine =
          displayObjects[DisplayObjectNames.BET_RESULT_SUMMARY];
        summaryToaster = await summaryDefine?.init({
          gameCore,
          width: summarySprite.width + 16,
        });
        if (!summaryToaster) {
          return;
        }
        summaryToaster.name = DisplayObjectNames.BET_RESULT_SUMMARY;
        summaryToaster.position.set(geometry.position.x, 56);
        summaryToaster.addChild(summarySprite);

        const resultToaster =
          gameCore.displayObjects[DisplayObjectNames.BET_RESULT_TOASTER];
        if (!resultToaster) {
          return;
        }
        resultToaster.addChild(summaryToaster);
      };

      const removeVideoSprite = ({ gameCore, name }) => {
        const sprite = gameCore.displayObjects[name];
        if (sprite) {
          sprite.visible = false;
          const video = sprite.texture.baseTexture.resource?.source;
          if (video) {
            video.currentTime = 0;
          }
        }
      };

      const handleVideoEnded = async event => {
        switch (event.detail?.name) {
          case DomResourceNames.ROLL_VIDEO_START:
          case DomResourceNames.ROLL_VIDEO_MIDDLE: {
            const rollEndSprite =
              gameCore.displayObjects[DisplayObjectNames.ROLL_VIDEO_END_SPRITE];
            if (rollEndSprite) {
              try {
                const endVideo =
                  rollEndSprite.texture.baseTexture.resource.source;
                endVideo.currentTime = 0;
                await endVideo.play();
                rollEndSprite.visible = true;
              } catch (error) {
                gameError('play roll end video fail', {
                  error,
                  event,
                  gameCore,
                });
              }
            }

            removeVideoSprite({
              gameCore,
              name:
                DomResourceNames.ROLL_VIDEO_START === event.detail.name
                  ? DisplayObjectNames.ROLL_VIDEO_START_SPRITE
                  : DisplayObjectNames.ROLL_VIDEO_MIDDLE_SPRITE,
            });
            break;
          }
          case DomResourceNames.ROLL_VIDEO_END: {
            removeVideoSprite({
              gameCore,
              name: DisplayObjectNames.ROLL_VIDEO_END_SPRITE,
            });

            // display draw result toaster
            [
              DisplayObjectNames.DRAW_RESULT_DICE_1ST,
              DisplayObjectNames.DRAW_RESULT_DICE_2ND,
              DisplayObjectNames.DRAW_RESULT_DICE_3RD,
              DisplayObjectNames.DRAW_RESULT_CONTENT,
            ].forEach(name => {
              const object = gameCore.displayObjects[name];
              if (!object) {
                return;
              }
              const objectSize = { x: object.width, y: object.height };
              object.visible = true;
              gsap.effects.flip(object, {
                width: objectSize.x,
                height: objectSize.y,
                onComplete: () => {
                  object.visible = false;
                  object.width = objectSize.x;
                  object.height = objectSize.y;
                },
              });
            });
            gameCore.eventTarget?.viewOpen({
              name: DisplayObjectNames.DRAW_RESULT_CONTENT,
              target:
                gameCore.displayObjects[DisplayObjectNames.DRAW_RESULT_CONTENT],
              extraProps: {
                'sic_bo.id': gameCore.currentGame?.id,
                'sic_bo.dices': gameCore.currentGame?.rollResults,
                'sic_bo.results': [odd],
              },
            });

            // display bet result win toaster
            const winToaster =
              gameCore.displayObjects[
                DisplayObjectNames.BET_RESULT_WIN_TOASTER
              ];
            if (earnedPoints && winToaster) {
              setTimeout(() => {
                winToaster.visible = true;
                winToaster.tween?.restart();

                gameCore.eventTarget?.viewOpen({
                  name: DisplayObjectNames.BET_RESULT_WIN_TOASTER,
                  target: winToaster,
                  extraProps: {
                    'sic_bo.id': gameCore.currentGame?.id,
                    'sic_bo.bet.reward': earnedPoints,
                  },
                });
              }, TOASTER_DURATION_MSEC);
            }

            // display bet summary
            setTimeout(() => {
              if (!summaryToaster) {
                return;
              }
              summaryToaster.visible = true;
              summaryToaster.tween?.restart();

              gameCore.eventTarget?.viewOpen({
                name: DisplayObjectNames.BET_RESULT_SUMMARY,
                target: summaryToaster,
                extraProps: {
                  'sic_bo.id': gameCore.currentGame?.id,
                  'sic_bo.bet.amount': betTotal,
                  'sic_bo.bet.reward': earnedPoints,
                },
              });
            }, TOASTER_DURATION_MSEC);
            break;
          }
          default:
            break;
        }
      };

      [{ type: 'videoEnded', listener: handleVideoEnded }].forEach(
        ({ type, listener }) => {
          gameCore.eventTarget.addEventListener(type, listener);
          gameCore.scene.eventListeners.push({ type, listener });
        }
      );

      Promise.all([
        updateDrawResultToaster(),
        updateBetResultWinToaster(),
        createBetSummary(),
      ]);
    },
    tick:
      ({ gameCore }) =>
      () => {
        chipsTick({ gameCore });
      },
    exit: ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      if (sceneTitle) {
        sceneTitle.text = '';
      }
    },
  },
  [GameStateEnum.RESETTING]: {
    name: GameStateEnum.RESETTING,
    renderTree: {
      name: DisplayObjectNames.ROOT,
      children: [
        BackgroundRenderTree,
        TableRenderTree,
        ForegroundRenderTree,
        UiRenderTree,
        ToasterRenderTree,
      ],
    },
    init: async () => {
      await pixi.Assets.loadBundle(GameStateEnum.RESETTING);
    },
    onTreeSynced: async ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      if (sceneTitle && sceneTitle.text !== GameStateEnum.RESETTING) {
        sceneTitle.text = GameStateEnum.RESETTING;
      }

      // update bet numbers, should set to 0
      [
        {
          objectName: DisplayObjectNames.BIG_BETS_NUMBER,
          choice: BetChoiceEnum.BIG,
        },
        {
          objectName: DisplayObjectNames.SMALL_BETS_NUMBER,
          choice: BetChoiceEnum.SMALL,
        },
        {
          objectName: DisplayObjectNames.TRIPLES_BETS_NUMBER,
          choice: BetChoiceEnum.TRIPLES,
        },
      ].forEach(({ objectName, choice }) => {
        const numberObject = gameCore.displayObjects[objectName];
        if (numberObject) {
          const number = gameCore.user.bets
            .filter(bet => choice === bet.choice)
            .reduce((acc, curr) => {
              return acc + curr.amount;
            }, 0);
          const text = `${number}`;
          if (numberObject.text !== text) {
            numberObject.text = text;
          }
        }
      });

      const bettedChips =
        gameCore.dynamicDisplayObjects[DisplayObjectNames.BETTED_CHIPS]
          ?.children;
      if (bettedChips?.length) {
        const toPosition = { x: 0, y: 24 };
        const settleBorder = 2;
        bettedChips?.forEach(chip => {
          chip.tween = gsap.to(chip, {
            x: `random(${toPosition.x - settleBorder}, ${
              toPosition.x + settleBorder
            }, 1)`,
            y: `random(${toPosition.y - settleBorder}, ${
              toPosition.y + settleBorder
            }, 1)`,
            duration: 1.9,
            onComplete: () => {
              chip.removeFromParent();
              bettedChips.splice(bettedChips.indexOf(chip), 1);
              chip.tween.kill();
              chip.tween = null;
              chip.destroy();
            },
          });
        });
      }

      const titleText = i18n.t('sic_bo_clean_up', {
        ns: TranslationNamespace.LIVESTREAM,
      });
      const toasterTitle =
        gameCore.displayObjects[DisplayObjectNames.GAME_STATUS_TOASTER_TITLE];
      if (toasterTitle && toasterTitle.text !== titleText) {
        toasterTitle.text = titleText;
      }
      const toaster =
        gameCore.displayObjects[DisplayObjectNames.GAME_STATUS_TOASTER];
      if (toaster) {
        toaster.visible = true;
        toaster.tween?.restart();
      }
      const toasterBackground =
        gameCore.displayObjects[DisplayObjectNames.TOASTER_BACKGROUND];
      if (toasterBackground) {
        toasterBackground.visible = true;
        toasterBackground.tween?.restart();
      }
    },
    tick:
      ({ gameCore }) =>
      () => {
        chipsTick({ gameCore });
      },
    exit: ({ gameCore }) => {
      const sceneTitle =
        gameCore.displayObjects[DisplayObjectNames.SCENE_TITLE];
      if (sceneTitle) {
        sceneTitle.text = '';
      }
    },
  },
};

/**
 * Get game Sic-Bo core
 * @return {Object} game core.
 */
export const getGameCore = () => {
  return gameCore;
};

// Ref: https://stackoverflow.com/a/9436948
const isString = target => {
  return typeof target === 'string' || target instanceof String;
};

const syncDisplayObjectGeometry = ({
  targetGrometry: t,
  displayObject: o,
  rootSize: r,
}) => {
  if (!t || !o || !r) {
    return;
  }

  // anchor
  if (
    o instanceof pixi.Sprite &&
    (o.anchor.x !== t.anchor.x || o.anchor.y !== t.anchor.y)
  ) {
    o.anchor.set(t.anchor.x || 0, t.anchor.y || 0);
  }

  // position, use current value if no target x/y
  if (t.position) {
    const tPosition = {
      x: isString(t.position.x)
        ? +t.position.x.replace('px', '')
        : !t.position.x && t.position.x !== 0
          ? o.position.x
          : r.width * t.position.x,
      y: isString(t.position.y)
        ? +t.position.y.replace('px', '')
        : !t.position.y && t.position.y !== 0
          ? o.position.y
          : r.height * t.position.y,
    };
    if (o.position.x !== tPosition.x || o.position.y !== tPosition.y) {
      o.position.set(tPosition.x, tPosition.y);
    }
  }

  // size, don't assign value if no target x/y
  if (t.size?.x || t.size?.x === 0) {
    o.width = isString(t.size.x)
      ? +t.size.x.replace('px', '')
      : r.width * t.size.x;
  }
  if (t.size?.y || t.size?.y === 0) {
    o.height = isString(t.size.y)
      ? +t.size.y.replace('px', '')
      : r.height * t.size.y;
  }

  // skew
};

const syncGeometryTree = ({
  currentTree: c,
  targetTree: t,
  gameCore,
  depth = 64,
}) => {
  if (!c || !t || depth < 0) {
    return;
  }

  syncDisplayObjectGeometry({
    targetGrometry: t.geometry || displayObjects[c.name]?.geometry,
    displayObject: c,
    rootSize: {
      width: gameCore.app.screen.width,
      height: gameCore.app.screen.height,
    },
  });

  if (t.ignoreChildren) {
    return;
  }

  t.children?.forEach((child, index) => {
    return syncGeometryTree({
      currentTree: c.children[index],
      targetTree: child,
      gameCore,
      depth: depth - 1,
    });
  });

  return;
};

const syncRenderTree = async ({
  currentTree: c,
  targetTree: t,
  gameCore,
  depth = 64,
}) => {
  if (!c?.children || !t?.children || depth < 0 || t?.ignoreChildren) {
    return;
  }

  // sync child nodes
  const pendingChildren = await Promise.all(
    t.children
      .map((node, index) => ({ node, index })) // to keep index after filter
      .filter(({ node, index }) => {
        return node.name !== c.children[index]?.name;
      })
      .map(async ({ node, index }) => {
        let child = gameCore.displayObjects[node.name];
        // target child inited
        if (child) {
          child.removeFromParent();
        } else {
          child = await displayObjects[node.name]?.init({ gameCore });
          if (!child) {
            throw new Error(
              'init child fail, missing displayObject defination?'
            );
          }
          child.name = node.name;
          gameCore.displayObjects[node.name] = child;
        }
        return { index, child };
      })
  );
  pendingChildren.forEach(({ child, index }) => {
    if (c.children[index]) {
      c.removeChild(c.children[index]);
    }
    c.addChildAt(child, index);
  });

  // remove extra children
  const extraChildren = c.children.filter((_, index) => !t.children[index]);
  c.removeChild(...extraChildren);

  // iterate children tree
  return Promise.all(
    t.children.map((child, index) => {
      return syncRenderTree({
        currentTree: c.children[index],
        targetTree: child,
        gameCore,
        depth: depth - 1,
      });
    })
  );
};

/**
 * Set game Sic-Bo scene
 * @param {string} {sceneName} - scene name, should be from GameStateEnum.
 * @param {number} {startTimestamp} - scene start timestamp
 * @param {number} {endTimestamp} - scene end timestamp
 * @param {number} {ntpTimeOffset} - NTP time offset
 */
export const setScene = async ({
  sceneName,
  startTimestamp,
  endTimestamp,
  ntpTimeOffset = 0,
}) => {
  const log = gameDebugLog.extend('setScene');
  const errorLog = gameError.extend('setScene');

  try {
    log('start set scene', sceneName);
    const scene = Scenes[sceneName];
    if (!scene) {
      throw new Error(`Unknown scene name: ${sceneName}`);
    }

    if (gameCore.scene.name === sceneName) {
      log('cancelled, same scene name', sceneName);
      return;
    }

    log('start exit scene', gameCore.scene.name);
    const currentScene = Scenes[gameCore.scene.name];
    await currentScene?.exit?.({ gameCore });

    // cleanup scene event listeners
    gameCore.scene.eventListeners.forEach(({ type, listener }) => {
      gameCore.eventTarget.removeEventListener(type, listener);
    });
    gameCore.scene.eventListeners = [];

    if (gameCore.currentTick) {
      gameCore.app.ticker.remove(gameCore.currentTick);
    }
    log('done exit scene', gameCore.scene.name);

    log('applying scene', sceneName);
    gameCore.scene.name = sceneName;
    gameCore.scene.startTimestamp = startTimestamp;
    gameCore.scene.endTimestamp = endTimestamp;
    gameCore.scene.ntpTimeOffset = ntpTimeOffset;

    await scene.init?.({ gameCore });
    log('done init scene', sceneName);

    await syncRenderTree({
      currentTree: gameCore.app.stage,
      targetTree: scene.renderTree,
      gameCore,
    });
    log('done sync scene render tree', sceneName);

    syncGeometryTree({
      currentTree: gameCore.app.stage,
      targetTree: scene.renderTree,
      gameCore,
    });
    log('done sync scene geometry tree', sceneName);

    scene.onTreeSynced?.({ gameCore });
    log('tree synced callback', sceneName);

    if (scene.tick) {
      const tick = delta => {
        /*
        syncGeometryTree({
          currentTree: gameCore.app.stage,
          targetTree: scene.renderTree,
          gameCore,
        });
        */
        return scene.tick({ gameCore })(delta);
      };
      gameCore.app.ticker.add(tick);
      gameCore.currentTick = tick;
    }
    log('done applying scene', sceneName);

    log('done set scene', sceneName);
  } catch (error) {
    errorLog('set scene error', error);
  }
};

/**
 * Set game Sic-Bo state
 * @param {object} {gameState} - new game state
 */
export const setGameState = async ({ gameState }) => {
  const log = gameDebugLog.extend('setState');
  const errorLog = gameError.extend('setState');

  try {
    log('start set game state', gameState);

    gameCore.currentGame = {
      ...gameCore.currentGame,
      ...gameState,
    };

    if (
      gameCore.scene.name !== gameState?.gameStatus &&
      Scenes[gameState?.gameStatus]
    ) {
      setScene({
        sceneName: gameState?.gameStatus,
        startTimestamp: gameState.statusStartTimestamp,
        endTimestamp: gameState.statusEndTimestamp,
        ntpTimeOffset: gameState.ntpTimeOffset,
      });
    }

    log('done set game state', gameState);
  } catch (error) {
    errorLog('set game state error', error, gameState);
  }
};

const createNewBetToasters = async ({ gameCore, bets }) => {
  const log = gameDebugLog.extend('createNewBetToasters');
  const errorLog = gameError.extend('createNewBetToasters');
  /*
  BET_TOASTER_DIAMONS_SPRITE: 'betToasterDiamondSprite',
  */

  try {
    log('start create new bet toasters', { bets });

    const [choiceTextDefine, amountTextDefine, toasterDefine] = [
      DisplayObjectNames.BET_TOASTER_CHOICE_TEXT,
      DisplayObjectNames.BET_TOASTER_AMOUNT_TEXT,
      // DisplayObjectNames.BET_TOASTER_DIAMONS_SPRITE,
      DisplayObjectNames.BET_TOASTER,
    ].map(name => displayObjects[name]);

    const generatedToasters = await Promise.all(
      bets.map(async bet => {
        const [choiceText, amountText] = await Promise.all([
          choiceTextDefine?.init({ gameCore, bet }),
          amountTextDefine?.init({ gameCore, bet }),
        ]);

        // compute toaster width according to text width
        const toasterWidth = 9 + choiceText.width + 8 + amountText.width + 9;
        if (choiceText) {
          choiceText.name = DisplayObjectNames.BET_TOASTER_CHOICE_TEXT;
          choiceText.anchor.set(
            choiceTextDefine.geometry.anchor.x,
            choiceTextDefine.geometry.anchor.y
          );
          choiceText.position.x = -toasterWidth / 2 + 9;
        }
        if (amountText) {
          amountText.name = DisplayObjectNames.BET_TOASTER_AMOUNT_TEXT;
          amountText.anchor.set(
            amountTextDefine.geometry.anchor.x,
            amountTextDefine.geometry.anchor.y
          );
          amountText.position.x =
            -toasterWidth / 2 + 9 + (choiceText?.width || 0) + 8;
        }

        const toaster = await toasterDefine?.init({
          gameCore,
          width: toasterWidth,
        });
        toaster.name = DisplayObjectNames.BET_TOASTER;
        toaster.timestamp = bet.timestamp;
        toaster.addChild(choiceText, amountText);

        return toaster;
      })
    );
    const toasters = generatedToasters.filter(a => a);
    log('new toasters', toasters);

    const betToastersContainer =
      gameCore.displayObjects[DisplayObjectNames.BET_TOASTERS];
    log('container', betToastersContainer);
    betToastersContainer?.addChild(...toasters);
    const dynamicContainer =
      gameCore.dynamicDisplayObjects[DisplayObjectNames.BET_TOASTERS]?.children;
    dynamicContainer?.push(...toasters);

    toasters.forEach(toaster => {
      const { width, height, y } = toaster;
      toaster.tween = gsap.to(toaster, {
        keyframes: {
          '0%': {
            width: width * 0.8,
            height: height * 0.8,
            y: y + height,
            alpha: 0,
          },
          '10%': { width: width * 0.8, height: height * 0.8, y, alpha: 1 },
          '30%': { width, height },
          '90%': { alpha: 1 },
          '100%': { alpha: 0 },
        },
        duration: TOASTER_DURATION_MSEC / 1000,
        // ease: 'power4', // problematic ><
        onComplete: () => {
          toaster.removeFromParent();
          dynamicContainer.splice(dynamicContainer.indexOf(toaster), 1);
          toaster.tween.kill();
          toaster.tween = null;
          toaster.destroy();
        },
      });
    });

    log('done create new bet toasters', { bets });
  } catch (error) {
    errorLog('create new bet toasters error', error, bets);
  }
};
const createNewBettedChips = async ({ gameCore, bets, user = 'me' }) => {
  const log = gameDebugLog.extend('createNewBettedChips');
  const errorLog = gameError.extend('createNewBettedChips');

  try {
    log('start create new betted chips', { bets });

    const chipDefine = displayObjects[DisplayObjectNames.BETTED_CHIP];
    const settleBorder = 4;

    // TODO: reuse chips
    const generatedChips = await Promise.all(
      bets.map(async bet => {
        const chip = await chipDefine?.init({
          gameCore,
          assetName:
            DisplayObjectNames[
              `CHIPS_SELECTOR_${BetAmountNameMap[bet.amount]}_SPRITE`
            ],
        });

        if (!chip) {
          return;
        }

        chip.name = DisplayObjectNames.BETTED_CHIP;
        chip.timestamp = bet.timestamp;
        chip.user = user;
        chip.anchor.set(0.5);
        chip.width = 30;
        chip.height = 30;

        const [bettedChips, chipSelector, slot] = [
          'BETTED_CHIPS',
          `CHIPS_SELECTOR_${BetAmountNameMap[bet.amount]}_SPRITE`,
          `${bet.choice.toUpperCase()}_SLOT`,
        ].map(name => gameCore.displayObjects[DisplayObjectNames[name]]);
        const [bettedChipsPosition, chipSelectorPosition, slotPosition] = [
          bettedChips,
          chipSelector,
          slot,
        ].map(object => object?.getGlobalPosition(undefined, true));
        const fromPosition = {
          x: chipSelectorPosition.x - bettedChipsPosition.x,
          y: chipSelectorPosition.y - bettedChipsPosition.y,
        };
        const toPositionOrigin = {
          x: slotPosition.x - bettedChipsPosition.x,
          y: slotPosition.y - bettedChipsPosition.y,
        };
        // TODO: reuse tween across chips
        chip.tween = gsap.fromTo(
          chip,
          { x: fromPosition.x, y: fromPosition.y, width: 30, height: 30 },
          {
            x: `random(${toPositionOrigin.x + settleBorder}, ${
              toPositionOrigin.x + slot.width - settleBorder
            }, 1)`,
            // can't use slot.height due to it's been expended
            y: `random(${toPositionOrigin.y + settleBorder}, ${
              toPositionOrigin.y + 44 - settleBorder
            }, 1)`,
            width: 10,
            height: 10,
            duration: TOASTER_DURATION_MSEC / 1000,
            ease: 'expo',
            onComplete: () => {
              chip.tween.kill();
              chip.tween = null;
            },
          }
        );

        return chip;
      })
    );
    const chips = generatedChips.filter(a => a);

    const chipsContainer =
      gameCore.displayObjects[DisplayObjectNames.BETTED_CHIPS];
    log('container', chipsContainer);
    chipsContainer?.addChild(...chips);
    gameCore.dynamicDisplayObjects[
      DisplayObjectNames.BETTED_CHIPS
    ]?.children?.push(...chips);

    log('done create new betted chips', { bets });
  } catch (error) {
    errorLog('create new betted chips error', error, bets);
  }
};

/**
 * Set game Sic-Bo user state
 * @param {object} {userState} - new user state
 */
export const setUserState = async ({ userState }) => {
  const log = gameDebugLog.extend('setUserState');
  const errorLog = gameError.extend('setUserState');

  try {
    log('start set user state', { userState, oldState: gameCore.user });

    const oldUserBets = gameCore.user?.bets;

    gameCore.user = {
      ...userState,
    };

    const newBets = userState?.bets.filter(bet => {
      return !oldUserBets.find(oldBet => bet.timestamp === oldBet.timestamp);
    });
    if (newBets.length) {
      log('create new bets toaster', newBets);
      createNewBetToasters({ gameCore, bets: newBets });
      createNewBettedChips({ gameCore, bets: newBets });
    }

    const userDiamondDisplay =
      gameCore.displayObjects[DisplayObjectNames.USER_POINTS_NUMBER];
    if (userDiamondDisplay) {
      userDiamondDisplay.text = numberWithCommas({ number: userState.points });
    }

    log('done set user state', userState);
  } catch (error) {
    errorLog('set user state error', error, userState);
  }
};

/**
 * Game sic bo
 * @param {Object} {game} - param.
 */
const gameSicBo = ({ game } = {}) => {
  const log = gameDebugLog.extend('gameSicBo');
  log('calling gameSicBo', game);
};

export default gameSicBo;
